/**
 * Project Feature Manager
 *
 * Manages feature assignments for a specific GitLab project.
 * Renders schema-driven option forms instead of raw JSON.
 * When linked to a feature pack, shows read-only view with detach option.
 */

import { useState, type ReactElement } from 'react';
import { useAdminFeatures } from '../../hooks/useAdminFeatures';
import { useAdminPackDetail } from '../../hooks/useAdminPacks';
import {
  useProjectAssignments,
  useAssignFeature,
  useRemoveAssignment,
  useUpdateAssignment,
} from '../../hooks/useAdminAssignments';
import type { Assignment, Feature, LinkedProject } from '../../schemas/admin.schemas';
import {
  buildValues, computeSparseOverrides, hasUnconfiguredOptions,
} from '../../utils/featureOptionValues';
import { ProjectFeatureRow } from './ProjectFeatureRow';
import { MissingRuntimeConfigBanner } from './MissingRuntimeConfigBanner';
import { useMissingRuntimeConfig } from '../../hooks/useMissingRuntimeConfig';

interface ProjectFeatureManagerProps {
  projectId: string;
  projectName: string;
  packLink?: LinkedProject | null;
  onDetachPack?: () => void;
}

export function ProjectFeatureManager({
  projectId, projectName, packLink, onDetachPack,
}: ProjectFeatureManagerProps): ReactElement {
  const isPackLinked = packLink != null;
  const { data: allFeatures } = useAdminFeatures(false);
  const { data: assignments, isLoading } = useProjectAssignments(projectId);
  const { data: packDetail } = useAdminPackDetail(isPackLinked ? packLink.pack_id : '');
  const assignFeature = useAssignFeature();
  const removeAssignment = useRemoveAssignment();
  const updateAssignment = useUpdateAssignment();
  const { data: missing } = useMissingRuntimeConfig({ projectId });
  const missingByFeature = new Map((missing ?? []).map((m) => [m.feature_id, m]));

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [optionValues, setOptionValues] = useState<Record<string, unknown>>({});
  const [autoClearNote, setAutoClearNote] = useState<string | null>(null);

  const features = allFeatures ?? [];
  const assignmentMap = new Map<string, Assignment>();
  for (const a of assignments ?? []) { assignmentMap.set(a.feature_id, a); }

  // When pack-linked, the pack's items (and their options) are what builds
  const packOptions = new Map<string, Record<string, unknown> | null>();
  if (isPackLinked && packDetail) {
    for (const item of packDetail.items) { packOptions.set(item.feature_id, item.options); }
  }
  const packFeatureIds = new Set(packOptions.keys());
  const savedOptions = (featureId: string): Record<string, unknown> | null | undefined =>
    isPackLinked ? packOptions.get(featureId) : assignmentMap.get(featureId)?.options;

  // Compute dependency relationships
  const requiredByMap = new Map<string, string[]>();
  for (const f of features) {
    const isAssignedOrPacked = isPackLinked ? packFeatureIds.has(f.id) : assignmentMap.has(f.id);
    if (!isAssignedOrPacked) continue;
    for (const depName of f.dependencies) {
      const depFeature = features.find((df) => df.name === depName);
      if (depFeature) {
        const existing = requiredByMap.get(depFeature.id) ?? [];
        existing.push(f.display_name);
        requiredByMap.set(depFeature.id, existing);
      }
    }
  }

  const handleToggle = (featureId: string): void => {
    const existing = assignmentMap.get(featureId);
    if (existing) {
      const feature = features.find((f) => f.id === featureId);
      const dependentNames = requiredByMap.get(featureId);
      if (dependentNames && dependentNames.length > 0) {
        const confirmed = window.confirm(
          `Removing ${feature?.display_name ?? 'this feature'} will also remove: ${dependentNames.join(', ')}. Continue?`
        );
        if (!confirmed) return;
      }
      removeAssignment.mutate({ assignmentId: existing.id, projectId });
    } else {
      assignFeature.mutate({ projectId, featureId, projectName });
    }
  };

  const handleExpand = (feature: Feature): void => {
    if (expandedId === feature.id) { setExpandedId(null); return; }
    setOptionValues(buildValues(feature, savedOptions(feature.id)));
    setAutoClearNote(null);
    setExpandedId(feature.id);
  };

  const handleSaveOptions = (feature: Feature): void => {
    const assignment = assignmentMap.get(feature.id);
    if (!assignment) return;
    const overrides = computeSparseOverrides(optionValues, feature);
    updateAssignment.mutate({ assignmentId: assignment.id, options: overrides, projectId });
    setExpandedId(null);
  };

  if (isLoading) {
    return <div className="settings-loading">Loading assignments...</div>;
  }

  return (
    <div className="project-features">
      <h3 className="project-features__title">Assigned Features</h3>
      <MissingRuntimeConfigBanner items={missing ?? []}
        {...(packDetail ? { packName: packDetail.display_name } : {})} />
      {isPackLinked && (
        <div className="admin-page__sync-message" role="status"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            Linked to Feature Pack: <strong>{packDetail?.display_name ?? 'Loading...'}</strong>.
            Features are managed by the pack.
          </span>
          {onDetachPack && (
            <button type="button" className="feature-table__toggle" onClick={onDetachPack}>
              Detach from Pack
            </button>
          )}
        </div>
      )}
      {features.length === 0 ? (
        <p className="project-features__empty">
          No features available. Create features in the Feature Registry first.
        </p>
      ) : (
        <ul className="project-features__list project-features__list--project">
          <li className="project-features__header">
            <span className="project-features__header-assigned">Assigned</span>
            <span className="project-features__header-feature">Feature</span>
            <span className="project-features__header-version">Version</span>
            <span className="project-features__header-category">Category</span>
            <span className="project-features__header-gate">Gate</span>
            <span className="project-features__header-options" />
          </li>
          {features.map((feature) => {
            const isAssigned = isPackLinked ? packFeatureIds.has(feature.id) : assignmentMap.has(feature.id);
            const isExpanded = expandedId === feature.id;
            const assignment = assignmentMap.get(feature.id);
            return (
              <ProjectFeatureRow
                key={feature.id}
                feature={feature}
                projectId={projectId}
                projectName={projectName}
                isAssigned={isAssigned}
                isPackLinked={isPackLinked}
                isExpanded={isExpanded}
                unconfigured={!isPackLinked && isAssigned
                  && hasUnconfiguredOptions(feature, assignment?.options)}
                requiredBy={requiredByMap.get(feature.id)}
                missingItem={missingByFeature.get(feature.id)}
                optionValues={isExpanded ? optionValues : buildValues(feature, savedOptions(feature.id))}
                autoClearNote={autoClearNote}
                onToggle={() => { handleToggle(feature.id); }}
                onExpand={() => { handleExpand(feature); }}
                onOptionsChange={(next, note) => { setAutoClearNote(note); setOptionValues(next); }}
                onSaveOptions={() => { handleSaveOptions(feature); }}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
}
